import React, { useEffect, useState } from "react";
import { productsAPI, variantGroupsAPI } from "../services/api";
import { useI18n } from "../i18n";
import Modal from "./Modal";

const blankRow = () => ({ key: Math.random().toString(36).slice(2), label: "", stock: "" });

/**
 * "Turn into a range": a generic product ("Mesh vsee") is replaced by a range
 * whose variants ("0.6Ω", "0.8Ω") each carry the count the owner made on the
 * shelf. Nothing is taken from the generic product's stock — the owner is
 * counting again — and the product is archived, its history kept.
 */
const ConvertToRangeDialog = ({ product, onClose, onConverted }) => {
  const { t } = useI18n();
  const [name, setName] = useState(product.name);
  const [rows, setRows] = useState([blankRow(), blankRow()]);
  const [packs, setPacks] = useState([]);
  const [replaceInPacks, setReplaceInPacks] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // A pack built from this product can hand the choice to the cashier instead
  useEffect(() => {
    let cancelled = false;

    productsAPI
      .getDeleteImpact(product.id)
      .then(({ data }) => {
        if (!cancelled) setPacks(data.packs || []);
      })
      .catch(() => {
        // Without it the form still works; the server refuses a packed product
      });

    return () => {
      cancelled = true;
    };
  }, [product.id]);

  const update = (key, patch) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  // Rows left blank are ignored, so the owner can leave a spare one
  const filled = rows.filter((row) => row.label.trim() !== "" || row.stock.trim() !== "");

  const validate = () => {
    if (filled.length === 0 || filled.some((row) => row.label.trim() === "")) {
      return t("products.convert.needVariant");
    }

    const seen = new Set();
    for (const row of filled) {
      const key = row.label.trim().toLowerCase();
      if (seen.has(key)) return t("products.convert.duplicate", { label: row.label.trim() });
      seen.add(key);

      if (row.stock.trim() !== "" && !/^\d+$/.test(row.stock.trim())) {
        return t("products.convert.badCount", { label: row.label.trim() });
      }
    }

    return "";
  };

  const submit = async (event) => {
    event.preventDefault();

    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }

    setSaving(true);
    setError("");

    try {
      const { data } = await variantGroupsAPI.convertProduct(product.id, {
        name: name.trim() || product.name,
        variants: filled.map((row) => ({
          label: row.label.trim(),
          stock_quantity: row.stock.trim() === "" ? 0 : Number(row.stock),
        })),
        ...(packs.length > 0 ? { replace_in_packs: replaceInPacks } : {}),
      });

      onConverted(data, filled.length);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          t("products.convert.failed"),
      );
      setSaving(false);
    }
  };

  const fieldClass = "px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500";
  const inputClass = `w-full ${fieldClass}`;

  return (
    <Modal title={t("products.convert.title")} onClose={onClose} maxWidthClass="max-w-xl">
      <form onSubmit={submit} className="space-y-5">
        <p className="text-sm text-gray-600">{t("products.convert.intro", { name: product.name })}</p>

        {error && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">
            {t("products.convert.rangeName")}
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputClass}
            maxLength={255}
          />
        </label>

        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">{t("products.convert.variantsTitle")}</p>

          <div className="space-y-2">
            {rows.map((row, index) => (
              <div key={row.key} className="flex items-center gap-2">
                <input
                  aria-label={t("products.convert.label")}
                  value={row.label}
                  onChange={(e) => update(row.key, { label: e.target.value })}
                  placeholder={t("products.convert.labelPlaceholder")}
                  maxLength={100}
                  autoFocus={index === 0}
                  className={`${fieldClass} min-w-0 flex-1`}
                />
                <input
                  aria-label={t("products.convert.count")}
                  value={row.stock}
                  onChange={(e) => update(row.key, { stock: e.target.value })}
                  placeholder={t("products.convert.count")}
                  inputMode="numeric"
                  className={`${fieldClass} w-24 shrink-0 text-center`}
                />
                <button
                  type="button"
                  title={t("products.convert.removeRow")}
                  aria-label={t("products.convert.removeRow")}
                  onClick={() =>
                    setRows((current) =>
                      current.length > 1 ? current.filter((r) => r.key !== row.key) : [blankRow()],
                    )
                  }
                  className="px-2 text-gray-400 hover:text-red-600 text-xl leading-none"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setRows((current) => [...current, blankRow()])}
            className="mt-3 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            {t("products.convert.addRow")}
          </button>
        </div>

        {packs.length > 0 && (
          <label className="flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-50 p-3 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={replaceInPacks}
              onChange={(e) => setReplaceInPacks(e.target.checked)}
              className="mt-1 h-4 w-4"
            />
            <span>
              {t("products.convert.inPacks", {
                packs: packs.map((pack) => pack.name).join(", "),
              })}
            </span>
          </label>
        )}

        <p className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
          {t("products.convert.willHappen", {
            name: product.name,
            stock: product.stock_quantity,
          })}
        </p>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold disabled:opacity-50"
          >
            {saving ? t("products.convert.submitting") : t("products.convert.submit")}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ConvertToRangeDialog;
